// src/pages/FAQ.js - COMPLETE OPTIMIZED FAQ PAGE
import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, HelpCircle, Mail, Phone, MapPin } from 'lucide-react';

export default function FAQ() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  const faqs = useMemo(() => [
    {
      category: "General",
      icon: "🏢",
      questions: [
        {
          q: "What services does NEVXOAULIO VENTURES offer?",
          a: "We offer comprehensive technology solutions including Software Development, VLSI Design, Cloud Computing, Cyber Security, AI/ML Solutions, Data Analytics, and specialized training and consultancy services."
        },
        {
          q: "Where is NEVXOAULIO VENTURES located?",
          a: "We are located in Tumkur, Karnataka, India. Our address is: 2nd Floor, Shylashree Nilaya, 2nd cross, Banashankari, Gandinagar Tumkur, Tumkur- 572102, India."
        },
        {
          q: "How can I contact NEVXOAULIO VENTURES?",
          a: "You can reach us via phone at +91 94490 61503, email at info@nevxoaulio.com, or through our contact form on the website. We're available Monday to Saturday, 9:00 AM - 6:00 PM IST."
        },
        {
          q: "Is NEVXOAULIO VENTURES a registered company?",
          a: "Yes, we are officially registered with the Ministry of Corporate Affairs (CIN: U62011KA2025OPC202304) and certified by MSME (UDYAM-KR-25-0083581) and Startup India (DIPP209630)."
        }
      ]
    },
    {
      category: "Services & Projects",
      icon: "🛠️",
      questions: [
        {
          q: "What is your project delivery timeline?",
          a: "Project timelines vary based on complexity and requirements. Typically, small projects take 2-4 weeks, medium projects 1-3 months, and large enterprise solutions 3-6 months. We provide detailed timelines during the consultation phase."
        },
        {
          q: "Do you provide custom software development?",
          a: "Yes, we specialize in custom software development tailored to your specific business needs. Our team works closely with clients to understand requirements and deliver solutions that align with their goals."
        },
        {
          q: "Can you handle VLSI design projects?",
          a: "Absolutely! We have expertise in ASIC Design, FPGA Development, RTL Design & Verification, and Physical Design. We've successfully delivered multiple VLSI projects across various technology nodes."
        },
        {
          q: "Do you offer support after project completion?",
          a: "Yes, we provide ongoing support and maintenance for all projects. Our support packages include bug fixes, updates, performance monitoring, and technical assistance as needed."
        },
        {
          q: "What industries do you serve?",
          a: "We serve various industries including Healthcare, Finance, E-commerce, Manufacturing, Education, Semiconductor, and more. Our solutions are adaptable to any industry's specific requirements."
        }
      ]
    },
    {
      category: "Technology & Tools",
      icon: "💻",
      questions: [
        {
          q: "What technologies do you work with?",
          a: "We work with a wide range of technologies including React, Node.js, Python, Java, AWS, Azure, TensorFlow, PyTorch, Verilog, SystemVerilog, and many more. Our team stays updated with the latest technology trends."
        },
        {
          q: "Do you use Agile methodology?",
          a: "Yes, we follow Agile development practices with regular sprints, continuous feedback, and iterative improvements. This ensures transparency and allows clients to be involved throughout the development process."
        },
        {
          q: "Can you integrate with existing systems?",
          a: "Yes, we have extensive experience integrating new solutions with existing systems. We ensure seamless integration with minimal disruption to your current operations."
        }
      ]
    },
    {
      category: "Pricing & Payment",
      icon: "💰",
      questions: [
        {
          q: "How do you charge for projects?",
          a: "Pricing depends on project scope, complexity, and timeline. We offer flexible pricing models including fixed-price, time & material, and dedicated team options. Contact us for a detailed quote."
        },
        {
          q: "Do you offer free consultations?",
          a: "Yes, we provide free initial consultations to understand your requirements and suggest the best solutions. This helps us provide accurate estimates and project plans."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept bank transfers, UPI, and online payment methods. Payment terms are typically milestone-based for larger projects and upfront for smaller engagements."
        },
        {
          q: "Do you provide project estimates?",
          a: "Yes, after understanding your requirements, we provide detailed project estimates including timeline, resources, and costs. We ensure transparency in all our pricing."
        }
      ]
    },
    {
      category: "Security & Compliance",
      icon: "🔒",
      questions: [
        {
          q: "How do you ensure data security?",
          a: "We implement industry-standard security practices including encryption, secure coding practices, regular security audits, and compliance with data protection regulations like GDPR."
        },
        {
          q: "Do you sign NDAs?",
          a: "Yes, we understand the importance of confidentiality and are happy to sign NDAs before discussing your project details. Your intellectual property is protected."
        },
        {
          q: "Are your solutions GDPR compliant?",
          a: "Yes, we ensure all our solutions comply with relevant data protection regulations including GDPR, ensuring your users' data is handled securely and legally."
        }
      ]
    },
    {
      category: "Getting Started",
      icon: "🚀",
      questions: [
        {
          q: "How do I start a project with you?",
          a: "Simply contact us through our website, email, or phone. We'll schedule a consultation to discuss your requirements, provide an estimate, and once approved, we'll kick off the project."
        },
        {
          q: "What information do you need to get started?",
          a: "We typically need project objectives, target audience, feature requirements, timeline expectations, and budget range. The more details you provide, the better we can plan."
        },
        {
          q: "Can I see examples of your previous work?",
          a: "Yes, please check our Portfolio page for detailed case studies of our completed projects. We can also arrange calls with references from satisfied clients."
        },
        {
          q: "Do you work with startups?",
          a: "Absolutely! We love working with startups and offer special packages to help early-stage companies bring their ideas to life within budget constraints."
        }
      ]
    }
  ], []);

  const toggleAccordion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setActiveIndex(activeIndex === index ? null : index);
  };

  const filteredFaqs = useMemo(() => {
    return faqs.map(category => ({
      ...category,
      questions: category.questions.filter(faq =>
        faq.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.questions.length > 0);
  }, [faqs, searchTerm]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* ===== HERO SECTION ===== */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block p-4 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <HelpCircle className="w-16 h-16" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Frequently Asked Questions
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Find answers to common questions about our services, processes, and company
            </p>
          </div>
        </div>
      </section>

      {/* ===== SEARCH SECTION ===== */}
      <section className="py-12 bg-gray-50 dark:bg-gray-800 sticky top-20 z-30 shadow-md">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for answers..."
              className="w-full pl-12 pr-4 py-4 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-700 dark:text-white text-lg shadow-sm"
              aria-label="Search FAQs"
            />
          </div>
          {searchTerm && (
            <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Found <strong>{filteredFaqs.reduce((sum, cat) => sum + cat.questions.length, 0)}</strong> matching results
            </div>
          )}
        </div>
      </section>

      {/* ===== FAQ CONTENT ===== */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-20">
              <div className="inline-block p-8 bg-gray-100 dark:bg-gray-800 rounded-full mb-6">
                <HelpCircle className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                No Results Found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
                No results found for "<strong>{searchTerm}</strong>". Try different keywords or browse all categories.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="space-y-12">
              {filteredFaqs.map((category, categoryIndex) => (
                <div key={categoryIndex} className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
                  {/* Category Header */}
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === categoryIndex ? null : categoryIndex)}
                    className="w-full px-6 py-6 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-4 text-left">
                      <span className="text-4xl">{category.icon}</span>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                          {category.category}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {category.questions.length} questions
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {expandedCategory === categoryIndex ? (
                        <ChevronUp className="w-6 h-6 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Category Content */}
                  {expandedCategory === categoryIndex && (
                    <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="p-6 space-y-4">
                        {category.questions.map((faq, questionIndex) => {
                          const index = `${categoryIndex}-${questionIndex}`;
                          const isActive = activeIndex === index;

                          return (
                            <div
                              key={questionIndex}
                              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-all"
                            >
                              <button
                                onClick={() => toggleAccordion(categoryIndex, questionIndex)}
                                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                              >
                                <span className="text-lg font-semibold text-gray-900 dark:text-white pr-4">
                                  {faq.q}
                                </span>
                                {isActive ? (
                                  <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                )}
                              </button>

                              {isActive && (
                                <div className="px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-t border-gray-100 dark:border-gray-700">
                                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-base">
                                    {faq.a}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== CONTACT CTA SECTION ===== */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 text-center">
              Still Have Questions?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 text-center max-w-2xl mx-auto">
              We're here to help! Reach out to our team for personalized assistance with any questions or project inquiries.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Phone */}
              <a
                href="tel:+919449061503"
                className="flex flex-col items-center p-6 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 hover:shadow-lg transition-shadow border border-blue-200 dark:border-blue-800"
              >
                <Phone className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Phone</h3>
                <p className="text-blue-600 dark:text-blue-400 font-medium">+91 94490 61503</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">Mon-Sat, 9:00 AM - 6:00 PM IST</p>
              </a>

              {/* Email */}
              <a
                href="mailto:info@nevxoaulio.com"
                className="flex flex-col items-center p-6 rounded-lg bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 hover:shadow-lg transition-shadow border border-purple-200 dark:border-purple-800"
              >
                <Mail className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Email</h3>
                <p className="text-purple-600 dark:text-purple-400 font-medium text-sm">info@nevxoaulio.com</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">Respond within 24 hours</p>
              </a>

              {/* Location */}
              <a
                href="https://maps.google.com/?q=Tumkur+Karnataka"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center p-6 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 hover:shadow-lg transition-shadow border border-green-200 dark:border-green-800"
              >
                <MapPin className="w-8 h-8 text-green-600 mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Location</h3>
                <p className="text-green-600 dark:text-green-400 font-medium text-sm text-center">Tumkur, Karnataka</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">India</p>
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contact"
                className="px-8 py-4 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
              >
                Contact Us Now
              </a>
              <a
                href="/"
                className="px-8 py-4 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}